/**
 * Brands Slice
 *
 * Brand showcase grid with cards.
 * Brands managed via repeatable items in Prismic.
 */

import React from "react";
import { graphql } from "gatsby";
import { motion } from "framer-motion";
import { PrismicRichText } from "@prismicio/react";
import BrandCard from "../../components/BrandCard";
import Icon from "../../components/Icon";
import BackgroundStaticVideo from "../../components/BackgroundStaticVideo";
import * as styles from "./Brands.module.scss";

export const Brands = ({ slice }) => {
  const { heading, copy, cta_text, cta_link, color_scheme, show_background_static_video } =
    slice?.primary || {};
  const items = slice?.items || [];

  return (
    <section
      className={styles.brands}
      data-color-scheme={color_scheme || "green"}
      data-background-static-video={show_background_static_video ? "true" : undefined}
      id="brands"
    >
      {show_background_static_video && <BackgroundStaticVideo />}
      <div className={`page-width--slim padding-section-vertical ${styles.container}`}>
        {/* Header */}
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {heading?.richText && (
            <PrismicRichText
              field={heading.richText}
              components={{
                heading2: ({ children }) => (
                  <h2 className="h2 text-center">{children}</h2>
                ),
              }}
            />
          )}
          {copy?.richText && (
            <div className={styles.copy}>
              <PrismicRichText
                field={copy.richText}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-body-xl text-center">{children}</p>
                  ),
                }}
              />
            </div>
          )}
        </motion.div>

        {/* Brand Grid */}
        {items.length > 0 && (
          <motion.ul
            className={styles.grid}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {items.map((brand, index) => (
              <li key={index}>
                <BrandCard brand={brand} />
              </li>
            ))}
          </motion.ul>
        )}

        {/* CTA Link */}
        {cta_text && cta_link?.url && (
          <motion.a
            href={cta_link.url}
            target={cta_link.target}
            rel={cta_link.target === "_blank" ? "noopener noreferrer" : undefined}
            className={styles.cta}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {cta_text} <Icon name="external" size="0.9em" />
          </motion.a>
        )}
      </div>
    </section>
  );
};

export const query = graphql`
  fragment HomepageDataBodyBrands on PrismicHomepageDataBodyBrands {
    primary {
      heading {
        richText
        text
      }
      copy {
        richText
        text
      }
      cta_text
      cta_link {
        url
        target
      }
      color_scheme
      show_background_static_video
    }
    items {
      brand_name
      brand_logo {
        url
        alt
      }
      brand_description
      brand_background_color
      brand_link {
        url
        target
      }
      instagram_url
      twitter_url
      tiktok_url
      youtube_url
      facebook_url
    }
  }
`;
